import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, InlineLoading, InlineNotification, Layer, Stack, TextInputSkeleton, Toggle } from '@carbon/react';
import OpeningHoursField, { OpeningHours, toOpeningHoursPayload } from 'src/components/Branch/OpeningHoursField';
import { useClearSavedState } from 'src/hooks/useClearSavedState';
import { useStorefrontBranchOrderingMutation, useStorefrontBranches } from 'src/hooks/useStorefront';
import { RestaurantBranchEntity } from 'src/services/apis/types';

interface BranchOrderingCardProps {
  branch: RestaurantBranchEntity;
  saving: boolean;
  onSave: (payload: { branchId: string; publicOrderingEnabled: boolean; publicOrderingHours: OpeningHours }) => void;
}

/**
 * One branch's ordering switch and window.
 *
 * State is seeded once from the loaded row and owned from then on, the same rule `SectionsEditor`
 * and `ExtrasEditor` follow: the refetch a save triggers must not yank a half-edited window out
 * from under whoever is editing it.
 */
const BranchOrderingCard = React.memo(({ branch, saving, onSave }: BranchOrderingCardProps) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(Boolean(branch.publicOrderingEnabled));
  const [hours, setHours] = useState<OpeningHours>((branch.publicOrderingHours as OpeningHours) ?? {});

  const handleSave = useCallback(() => {
    onSave({
      branchId: branch.id,
      publicOrderingEnabled: enabled,
      publicOrderingHours: toOpeningHoursPayload(hours),
    });
  }, [onSave, branch.id, enabled, hours]);

  return (
    // A card like the Extras items: on $layer, with a `Layer` inside so the fields step up from it.
    <div className="storefront-ordering__branch">
      <Layer>
        <h4 className="storefront-ordering__branchName">{branch.name}</h4>

        <Toggle
          id={`publicOrderingEnabled-${branch.id}`}
          labelText={t('storefront.ordering.enabled')}
          labelA={t('storefront.ordering.enabledOff')}
          labelB={t('storefront.ordering.enabledOn')}
          toggled={enabled}
          onToggle={setEnabled}
        />

        {/* Shown only when ordering is on, because the window is meaningless otherwise — and
          leaving it visible invites someone to set hours and assume that switched ordering on. */}
        {enabled ? (
          <>
            <p className="cds--form__helper-text">{t('storefront.ordering.hoursHint')}</p>
            <OpeningHoursField value={hours} onChange={setHours} />
          </>
        ) : null}

        {/* Hidden while ordering is off and was already off: there is nothing to save. Kept when a
          branch that takes orders is switched off, or that change could never be saved. */}
        {enabled || branch.publicOrderingEnabled ? (
          <Button className="storefront-ordering__save" type="button" onClick={handleSave} disabled={saving}>
            {t('storefront.actions.save')}
          </Button>
        ) : null}
      </Layer>
    </div>
  );
});

BranchOrderingCard.displayName = 'BranchOrderingCard';

/**
 * Which branches take orders from the public website, and when.
 *
 * These two columns gate the storefront's `/menu` behind `PublicTenantGuard`, and until this
 * screen existed they were editable NOWHERE — `restaurantBranch.dto.ts` says so in as many
 * words. A restaurant could publish a site whose menu page permanently read "not available",
 * with no way to change it short of a database write.
 *
 * Deliberately here rather than on the branch form. The branch DTO's omission of these columns is
 * a documented invariant with two guard tests behind it, and the reason holds: making a branch
 * publicly orderable is storefront configuration, not day-to-day branch editing, so it belongs
 * behind the storefront area's own gate (owner or platform admin) rather than behind whoever may
 * rename a branch.
 *
 * Saved per branch rather than all at once: the rows are independent, a partial failure should
 * not lose the others' edits, and each save revalidates the public site on the server.
 */
const StorefrontOrdering = React.memo(() => {
  const { t } = useTranslation();
  const { data: branches, isLoading } = useStorefrontBranches();
  const {
    mutate: saveOrdering,
    isLoading: saving,
    isSuccess: saved,
    error,
    reset,
  } = useStorefrontBranchOrderingMutation();

  useClearSavedState(saved, reset);

  return (
    <Stack gap={6}>
      <p className="storefront-ordering__hint">{t('storefront.ordering.hint')}</p>
      {isLoading && <TextInputSkeleton />}

      {!isLoading && (branches?.length ?? 0) === 0 ? (
        <p className="storefront-ordering__empty">{t('storefront.ordering.noBranches')}</p>
      ) : null}

      {!isLoading &&
        branches?.map((branch) => (
          <BranchOrderingCard key={branch.id} branch={branch} saving={saving} onSave={saveOrdering} />
        ))}

      {saving || saved ? (
        <InlineLoading
          description={saved ? t('common.saved') : `${t('common.saving')}...`}
          status={saved ? 'finished' : 'active'}
        />
      ) : null}
      {error ? <InlineNotification kind="error" subtitle={t('messages.somethingWentWrong')} /> : null}
    </Stack>
  );
});

export default StorefrontOrdering;
